import React, { useEffect } from 'react';
import {
  isCancelledError,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { reportError } from '@/lib/telemetry';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { Query } from '@tanstack/react-query';
import { getLocalDateString } from '@/utils/dateUtils';
import type { QueryError } from './types';
import { hasStatus } from './types';

type E2EWindow = Window & {
  __E2E_MOCK__?: boolean;
};

const isServerRender = typeof window === 'undefined';
const DAILY_CHALLENGE_CACHE_MAX_AGE = 1000 * 60 * 60 * 24;

/**
 * Provides TanStack Query configuration and persistence
 */
export function TanStackQueryProvider({ children }: { children: React.ReactNode }) {
  // Create the client once per component lifecycle
  const [queryClient] = React.useState(() => {
    const client = new QueryClient({
      queryCache: new QueryCache({
        onError: (error, query) => {
          const key = JSON.stringify(query.queryKey);
          if (!isCancelledError(error)) reportError({ error, source: `react-query:query:${key}` });
        },
      }),
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          const key = JSON.stringify(mutation.options.mutationKey ?? 'unknown');
          reportError({ error, source: `react-query:mutation:${key}` });
        },
      }),
      defaultOptions: {
        queries: {
          // Default options for all queries
          staleTime: 1000 * 60 * 5, // 5 minutes
          gcTime: isServerRender ? Infinity : undefined,
          // Exponential backoff for retries
          retry: (failureCount, error: QueryError) => {
            // Don't retry if we get a 404 or 400 error (not found, bad request)
            if (hasStatus(error) && (error.status === 404 || error.status === 400)) {
              return false;
            }
            // Otherwise retry up to 3 times with exponential backoff
            return failureCount < 3;
          },
          // Default refetch options - we prefer manual control
          refetchOnWindowFocus: false,
          refetchOnMount: true,
          refetchOnReconnect: true,
        },
        mutations: {
          // Default is no retries for mutations
          retry: false,
        },
      },
    });
    // Restored daily queries need the same lifetime before a query hook observes them.
    client.setQueryDefaults(['dailyChallengeQuestions'], {
      gcTime: isServerRender ? Infinity : DAILY_CHALLENGE_CACHE_MAX_AGE,
    });
    return client;
  });

  const [persistOptions] = React.useState(() => {
    if (typeof window === 'undefined') return null;

    try {
      return {
        persister: createSyncStoragePersister({
          storage: window.localStorage,
          key: 'trivia-query-cache',
          throttleTime: (window as E2EWindow).__E2E_MOCK__ ? 0 : 1000,
        }),
        maxAge: DAILY_CHALLENGE_CACHE_MAX_AGE,
        buster: `trivia-cache-v1-${getLocalDateString()}`,
        dehydrateOptions: {
          shouldDehydrateMutation: () => false,
          shouldDehydrateQuery: (query: Query) =>
            query.queryKey[0] === 'dailyChallengeQuestions' &&
            query.state.status === 'success' &&
            query.state.data !== undefined,
        },
      };
    } catch (error) {
      console.error('Failed to setup query persistence:', error);
      return null;
    }
  });

  // Devtools keyboard toggle
  const [isDevtoolsOpen, setIsDevtoolsOpen] = React.useState(false);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isModifierDown = e.metaKey || e.ctrlKey;
      if (e.shiftKey && isModifierDown && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setIsDevtoolsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const content = (
    <>
      {children}
      {process.env.NODE_ENV === 'development' ? (
        isDevtoolsOpen ? (
          <ReactQueryDevtools initialIsOpen={true} />
        ) : null
      ) : null}
    </>
  );

  return persistOptions ? (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      {content}
    </PersistQueryClientProvider>
  ) : (
    <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>
  );
}
